import { useApolloClient } from "@apollo/client";
import { Card, NonIdealState, Spinner } from "@blueprintjs/core";
import {
  useGetLocationQuery,
  useGetProfileQuery,
} from "@package/ui-gql/index-data-access";
import { AutoZoomFrame, Wall, WallContextProvider } from "@package/ui-wall";
import { WALL_HEIGHT_PREVIEW_AVAILABLE } from "../../constants/contants";
import { WallPreviewContainer } from "../WallPreviewContainer/WallPreviewContainer";
import { useAppStore } from "./../../store/store";

type WallPreviewLocationProps = {
  location?: string;
  version?: number | null;
};

export function WallPreviewLocation({
  location,
  version,
}: WallPreviewLocationProps) {
  const client = useApolloClient();
  const store = useAppStore();

  const {
    data: dataLocation,
    loading: loadingLocation,
    error: errorLocation,
  } = useGetLocationQuery({
    variables: {
      id: location ?? "",
    },
    skip: !location,
  });
  const {
    data: dataProfile,
    loading: loadingProfile,
    error: errorProfile,
  } = useGetProfileQuery({
    variables: {
      id: dataLocation?.item?.profile ?? "",
    },
    skip: !dataLocation?.item?.profile,
  });

  if (!location) {
    return (
      <WallPreviewContainer>
        <NonIdealState
          className="min-h-36"
          icon="info-sign"
          title="Select a Location to Preview"
        />
      </WallPreviewContainer>
    );
  }

  if (loadingLocation || loadingProfile) {
    return (
      <WallPreviewContainer>
        <Spinner className="min-h-36" />
      </WallPreviewContainer>
    );
  }

  if (
    errorLocation ||
    errorProfile ||
    !dataProfile?.item?.id ||
    !dataProfile?.item?.type ||
    !dataProfile?.item?.screen
  ) {
    return (
      <WallPreviewContainer>
        <NonIdealState
          className="min-h-36"
          icon="error"
          title="Location Canvas Misconfigured"
          description="Please check the selected canvas's screen configuration."
        />
      </WallPreviewContainer>
    );
  }

  return (
    <Card compact elevation={1}>
      <WallPreviewContainer key={version}>
        <AutoZoomFrame
          type={dataProfile.item.type}
          profileScreen={dataProfile.item.screen}
          height={WALL_HEIGHT_PREVIEW_AVAILABLE}
        >
          <WallContextProvider
            clientDataAccess={client}
            location={location}
            profile={dataProfile.item.id}
            store={store}
            type="local"
            caching={false}
          >
            <Wall />
          </WallContextProvider>
        </AutoZoomFrame>
      </WallPreviewContainer>
    </Card>
  );
}
